import axios from 'axios';
import { forgotPasswordBody, signupBody } from '@carboncare/schemas';
import { preflight } from './schemaPreflight';
import * as Sentry from '@sentry/react';
import { logger, metrics } from '../../config/sentry';
import {
  LoginCredentials,
  SignupData,
  AuthResponse,
  TwoFactorSetupResponse,
  TwoFactorVerifySetupData,
  TwoFactorVerifySetupResponse,
  TwoFactorVerifyData,
  TwoFactorDisableData,
  TwoFactorRegenerateBackupCodesData,
  TwoFactorStatusResponse,
} from '../../types/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag to prevent multiple redirects
let isRedirecting = false;

// Request interceptor to track request timing
apiClient.interceptors.request.use(
  (config) => {
    (config as any)._startTime = Date.now();
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    const startTime = (response.config as any)._startTime;
    if (startTime) {
      const duration = Date.now() - startTime;
      const url = response.config.url || 'unknown';
      metrics.distribution('api.response_time', duration, 'millisecond', { url, status: String(response.status) });
      metrics.increment('api.requests', 1, { url, status: String(response.status) });
    }
    return response;
  },
  (error) => {
    // Don't redirect on 401 for login/signup endpoints - let them handle their own errors
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') ||
      error.config?.url?.includes('/auth/signup');

    // Track API errors
    const status = error.response?.status;
    const url = error.config?.url || 'unknown';
    metrics.increment('api.errors', 1, { url, status: String(status || 0) });

    // Log and capture server errors (5xx) and unexpected errors
    if (!status || status >= 500) {
      logger.error('API server error', { url, method: error.config?.method, status });
      Sentry.captureException(error, {
        extra: {
          url: error.config?.url,
          method: error.config?.method,
          status,
          responseData: error.response?.data,
        },
      });
    } else if (status >= 400) {
      logger.warn('API client error', { url, method: error.config?.method, status });
    }

    if (error.response?.status === 401 && !isAuthEndpoint) {
      if (!isRedirecting) {
        // Token expired or invalid - clear all auth data
        isRedirecting = true;
        localStorage.clear(); // Clear ALL localStorage to remove stale user data

        // Set a timeout to allow the flag to reset before redirect
        setTimeout(() => {
          globalThis.location.href = '/login';
        }, 100);
      }
      // Reject the error without retrying
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  },

  signup: async (signupData: SignupData): Promise<AuthResponse> => {
    preflight(signupBody, signupData);
    const response = await apiClient.post('/auth/signup', signupData);
    return response.data;
  },

  refreshToken: async (): Promise<AuthResponse> => {
    const response = await apiClient.post('/auth/refresh-token');
    return response.data;
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    preflight(forgotPasswordBody, { email });
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, password: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/reset-password', { token, password });
    return response.data;
  },

  // ==================== 2FA TOTP Methods ====================

  setup2FA: async (): Promise<TwoFactorSetupResponse> => {
    const response = await apiClient.post('/auth/2fa/setup');
    return response.data.data;
  },

  verifySetup2FA: async (data: TwoFactorVerifySetupData): Promise<TwoFactorVerifySetupResponse> => {
    const response = await apiClient.post('/auth/2fa/verify-setup', data);
    return response.data.data;
  },

  verify2FA: async (data: TwoFactorVerifyData): Promise<AuthResponse> => {
    const response = await apiClient.post('/auth/2fa/verify', data);
    return response.data.data;
  },

  disable2FA: async (data: TwoFactorDisableData): Promise<{ message: string }> => {
    const response = await apiClient.post('/auth/2fa/disable', data);
    return response.data;
  },

  regenerateBackupCodes: async (data: TwoFactorRegenerateBackupCodesData): Promise<{ backupCodes: string[] }> => {
    const response = await apiClient.post('/auth/2fa/backup-codes/regenerate', data);
    return response.data.data;
  },

  get2FAStatus: async (): Promise<TwoFactorStatusResponse> => {
    const response = await apiClient.get('/auth/2fa/status');
    return response.data.data;
  },

  getMe: async (): Promise<any> => {
    const response = await apiClient.get('/users/me');
    return response.data;
  },
};

export { apiClient };